<template>
  <teleport to="body">
    <transition name="dialog-fade" @enter="onEnter" @leave="onLeave">
      <div v-if="visible" class="dlg-root" :style="{ zIndex: String(zIndex) }">
        <div class="dlg-mask" :style="maskStyle" @click="onMaskClick"></div>
        <div class="dlg-wrap">
          <QSection v-bind="$attrs" class="dlg-content">
            <slot :data="dataRef" :close="close" :confirm="confirmResolve" />
            <div v-if="$slots.cancel || $slots.confirm" class="dlg-actions">
              <slot name="cancel" :data="dataRef" :close="close" :confirm="confirmResolve"></slot>
              <slot name="confirm" :data="dataRef" :close="close" :confirm="confirmResolve"></slot>
            </div>
            <q-section v-if="closeable" v-bind="resolvedCloseButtonProps" @click="close"></q-section>
          </QSection>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watchEffect } from 'vue'
import QSection from '../../base/q-section.vue'

defineOptions({ name: 'QDialog', inheritAttrs: false })

type OpenOptions = {
  data?: any
  attrs?: Record<string, any>
  options?: { bgClose?: boolean }
  onclose?: () => void
}

const props = withDefaults(
  defineProps<{
    name: string
    cache?: boolean
    maskBgC?: string
    maskBgImg?: string
    aspectRatio?: number
    scale?: number
    zIndex?: number | string
    modelValue?: boolean
    maskClosable?: boolean
    closeable?: boolean
    closeButtonProps?: Record<string, unknown>
  }>(),
  {
    cache: false,
    maskBgC: 'rgba(0,0,0,0.7)',
    aspectRatio: 0.7,
    scale: 0.94,
    zIndex: 2100,
    modelValue: false,
    maskClosable: true,
    closeable: true,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'open'): void
  (e: 'close'): void
}>()

const visible = ref<boolean>(props.modelValue)
let bodyScrollLocked = false
watchEffect(() => {
  visible.value = props.modelValue
  if (props.modelValue) lockBodyScroll()
  else unlockBodyScroll()
})

const dataRef = ref<any>({})
const runtime = reactive<{ bgClose: boolean; onclose?: () => void }>({ bgClose: true })
const currentAttrs = ref<Record<string, any>>({})

const maskStyle = computed(() => {
  return props.maskBgImg
    ? { backgroundImage: `url(${props.maskBgImg})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: props.maskBgC }
})
const zIndex = computed(() => props.zIndex as number)
const resolvedCloseButtonProps = computed(() => ({ wh: '40', pr: '0', pt: '0', ...props.closeButtonProps }))

function open(opt?: OpenOptions): Promise<void> {
  if (opt?.attrs) currentAttrs.value = { ...currentAttrs.value, ...opt.attrs }
  if (opt?.data) dataRef.value = opt.data
  runtime.bgClose = !!opt?.options?.bgClose
  runtime.onclose = opt?.onclose
  if (!visible.value) emit('open')
  visible.value = true
  emit('update:modelValue', true)
  lockBodyScroll()
  return new Promise(resolve => requestAnimationFrame(() => resolve()))
}

function close(): Promise<void> {
  if (visible.value) emit('close')
  visible.value = false
  emit('update:modelValue', false)
  const cb = runtime.onclose
  runtime.onclose = undefined
  if (cb) cb()
  unlockBodyScroll()
  // 如果存在挂起的 confirm，且未通过 confirmResolve 决定，兜底为 false
  if (confirmPending && confirmPromiseResolve) {
    const resolve = confirmPromiseResolve
    confirmPromiseResolve = null
    confirmPending = false
    resolve(false)
  }
  return new Promise(resolve => requestAnimationFrame(() => resolve()))
}

function update(data: Record<string, any>) {
  dataRef.value = { ...dataRef.value, ...data }
}

let confirmPromiseResolve: ((v: boolean) => void) | null = null
let confirmPending = false
function confirm(opt?: OpenOptions): Promise<boolean> {
  confirmPending = true
  return new Promise<boolean>(res => {
    confirmPromiseResolve = res
    open(opt)
  })
}
function confirmResolve(v: boolean) {
  if (confirmPromiseResolve) {
    const resolve = confirmPromiseResolve
    confirmPromiseResolve = null
    confirmPending = false
    resolve(!!v)
  }
  close()
}

function onMaskClick() {
  // 优先使用 options.bgClose，其次使用 props.maskClosable
  if (runtime.bgClose ? runtime.bgClose : props.maskClosable) {
    close()
  }
}

// 动画钩子函数
function onEnter(el: Element, done: () => void) {
  const element = el as HTMLElement
  const wrap = element.querySelector('.dlg-wrap') as HTMLElement

  if (wrap) {
    // 初始状态：小且透明
    wrap.style.transform = 'translate(-50%, -50%) scale(0.3)'
    wrap.style.opacity = '0'

    // 强制重排
    wrap.offsetHeight

    // 动画到最终状态
    wrap.style.transition = 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
    wrap.style.transform = 'translate(-50%, -50%) scale(1)'
    wrap.style.opacity = '1'

    // 动画完成后调用 done
    setTimeout(done, 300)
  } else {
    done()
  }
}

function onLeave(el: Element, done: () => void) {
  const element = el as HTMLElement
  const wrap = element.querySelector('.dlg-wrap') as HTMLElement

  if (wrap) {
    // 动画到关闭状态
    wrap.style.transition = 'all 0.2s ease-in'
    wrap.style.transform = 'translate(-50%, -50%) scale(0.3)'
    wrap.style.opacity = '0'

    // 动画完成后调用 done
    setTimeout(done, 200)
  } else {
    done()
  }
}

defineExpose({ open, close, update, confirm })

onMounted(() => {
  const w = window as any
  w.__dialogs = w.__dialogs || {}
  const api = { open, close, update, confirm }
  w.__dialogs[props.name] = api
  if (w.__vue_app__ && w.__vue_app__.config) {
    w.__vue_app__.config.globalProperties.$dialogs = w.__dialogs
  }
})

onBeforeUnmount(() => {
  const w = window as any
  if (w.__dialogs && w.__dialogs[props.name]) {
    if (!props.cache) delete w.__dialogs[props.name]
  }
  // 确保卸载时解除滚动锁
  unlockBodyScroll()
})

// 页面滚动锁定（支持多弹窗叠加的引用计数）
function lockBodyScroll() {
  if (bodyScrollLocked) return
  bodyScrollLocked = true

  const w = window as any
  w.__dlg_lock_count = (w.__dlg_lock_count || 0) + 1
  if (w.__dlg_lock_count === 1) {
    const body = document.body
    const html = document.documentElement
    const scrollX = window.scrollX || html.scrollLeft || body.scrollLeft || 0
    const scrollY = window.scrollY || html.scrollTop || body.scrollTop || 0
    const pageHeight = Math.max(body.scrollHeight, html.scrollHeight)
    // 记录旧的样式以便恢复
    if (!w.__dlg_lock_backup) {
      w.__dlg_lock_backup = {
        bodyOverflow: body.style.overflow,
        bodyTouchAction: (body.style as any).touchAction,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyLeft: body.style.left,
        bodyRight: body.style.right,
        bodyWidth: body.style.width,
        bodyHeight: body.style.height,
        htmlOverflow: html.style.overflow,
        scrollX,
        scrollY,
      }
    }
    // 固定在当前滚动位置，避免部分 WebView 修改 overflow 后跳到页面顶部
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.left = `-${scrollX}px`
    body.style.right = '0'
    body.style.width = '100%'
    // fixed body 在部分 WebView 中会退化为视口高度，显式保留页面高度避免底部露出 html 白底
    body.style.height = `${pageHeight}px`
    body.style.overflow = 'hidden'
    ;(body.style as any).touchAction = 'none'
    html.style.overflow = 'hidden'
  }
}

function unlockBodyScroll() {
  if (!bodyScrollLocked) return
  bodyScrollLocked = false

  const w = window as any
  if (!w.__dlg_lock_count) return
  w.__dlg_lock_count = Math.max(0, w.__dlg_lock_count - 1)
  if (w.__dlg_lock_count === 0) {
    const body = document.body
    const html = document.documentElement
    const backup = w.__dlg_lock_backup || {}
    if (backup.bodyOverflow !== undefined) body.style.overflow = backup.bodyOverflow
    if (backup.bodyTouchAction !== undefined) (body.style as any).touchAction = backup.bodyTouchAction
    if (backup.bodyPosition !== undefined) body.style.position = backup.bodyPosition
    if (backup.bodyTop !== undefined) body.style.top = backup.bodyTop
    if (backup.bodyLeft !== undefined) body.style.left = backup.bodyLeft
    if (backup.bodyRight !== undefined) body.style.right = backup.bodyRight
    if (backup.bodyWidth !== undefined) body.style.width = backup.bodyWidth
    if (backup.bodyHeight !== undefined) body.style.height = backup.bodyHeight
    if (backup.htmlOverflow !== undefined) html.style.overflow = backup.htmlOverflow
    w.__dlg_lock_backup = undefined
    window.scrollTo(backup.scrollX || 0, backup.scrollY || 0)
  }
}
</script>

<style scoped>
.dlg-root {
  position: fixed;
  inset: 0;
}
.dlg-mask {
  position: absolute;
  inset: 0;
}
.dlg-wrap {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
.dlg-content {
  overflow: hidden;
}
.dlg-actions {
  display: flex;
  gap: 12px;
  padding: 12px;
  justify-content: flex-end;
}
.dlg-cancel,
.dlg-confirm {
  cursor: pointer;
  user-select: none;
}

/* 遮罩层动画 */
.dialog-fade-enter-active .dlg-mask,
.dialog-fade-leave-active .dlg-mask {
  transition: opacity 0.3s ease;
}

.dialog-fade-enter-from .dlg-mask,
.dialog-fade-leave-to .dlg-mask {
  opacity: 0;
}

.dialog-fade-enter-to .dlg-mask,
.dialog-fade-leave-from .dlg-mask {
  opacity: 1;
}
</style>
